import { zodResolver } from '@hookform/resolvers/zod'
import { Stack, TextColors, Typography } from '@sureco/design-system'
import RHFDatePicker from 'enrollment-platform/components/form/hookform/DatePicker/RHFDatePicker'
import RHFInput from 'enrollment-platform/components/form/hookform/Input/RHFInput'
import RHFMobileInput from 'enrollment-platform/components/form/hookform/MobileInput/RHFMobileInput'
import RHFSelect from 'enrollment-platform/components/form/hookform/Select/RHFSelect'
import useWindowDimensions from 'hooks/useWindowDimensions'
import ContentSection from 'layouts/ContentSection/ContentSection'
import dayjs from 'dayjs'
import React, { forwardRef, useImperativeHandle, useRef } from 'react'
import { useForm } from 'react-hook-form'
import {
  DEFAULT_BENEFIT_EFFECTIVE_DATE,
  DEMO_NAME_PREFIX,
  PAYROLL_CYCLE_OPTIONS,
} from '../constants'
import {
  DemoCustomerStep1Data as DemoCustomerCreateFormData,
  DemoCustomerStep1Schema as DemoCustomerCreateFormSchema,
} from '../demoCustomer.schema'

export type DemoFormRef = {
  submit: () => void
}

export interface DemoCustomerInitialConfig {
  /** Unprefixed display name (without "ZZ - DEMO - "). */
  companyName?: string
  /** Flattened from the customer's SURECO contact. */
  salesContact?: {
    fullName?: string
    title?: string
    phone?: string
    email?: string
  }
  // The fields below are display-only in edit mode. They are fixed at
  // creation time, so they render as text rather than inputs and are never
  // fed into form state or submitted.
  payrollCycle?: string
  /** YYYY-MM-DD strings from the enrollment configuration record. */
  benefitEffectiveDate?: string
  enrollmentStartDate?: string
  enrollmentEndDate?: string
}

interface DemoCustomerCreateFormProps {
  onSubmit: (data: DemoCustomerCreateFormData) => void
  /**
   * When provided the form is in edit mode: the name and sales contact are
   * seeded as editable inputs, while payroll cycle and the enrollment dates
   * render as static text because they are fixed at creation time.
   */
  initialConfig?: DemoCustomerInitialConfig
  /** Shows a placeholder for the read-only dates while they are loading. */
  enrollmentLoading?: boolean
}

/** Wide enough for the longest label ("Benefit Effective") and a full date. */
const READ_ONLY_FIELD_WIDTH = 120

function payrollCycleLabel(value?: string): string | undefined {
  if (!value) return undefined
  return PAYROLL_CYCLE_OPTIONS.find((o) => o.value === value)?.label ?? value
}

/**
 * Label-over-text field standing in for an input in edit mode. A fixed width
 * keeps the columns aligned — without it each one collapses to its own label
 * width and the values read as randomly spaced.
 */
function ReadOnlyField({
  label,
  value,
  width,
}: {
  label: string
  value?: string
  width: number
}) {
  return (
    <div style={{ minWidth: width }}>
      <Stack space={4}>
        <Typography size={13}>{label}</Typography>
        <Typography size={14} style={{ lineHeight: '30px', color: '#595959' }}>
          {value || '—'}
        </Typography>
      </Stack>
    </div>
  )
}

const DemoCustomerCreateForm = forwardRef<
  DemoFormRef,
  DemoCustomerCreateFormProps
>(({ onSubmit, initialConfig, enrollmentLoading = false }, ref) => {
  const isEditMode = Boolean(initialConfig)
  const formRef = useRef<HTMLFormElement>(null)
  const { width } = useWindowDimensions()
  const isSmallScreen = width < 600

  const { handleSubmit, control } = useForm<DemoCustomerCreateFormData>({
    resolver: zodResolver(DemoCustomerCreateFormSchema),
    defaultValues: {
      // Payroll cycle and the dates are only editable on create. In edit mode
      // these defaults just satisfy the schema; the edit flow never sends them.
      payrollCycle: '12',
      benefitEffectiveDate: DEFAULT_BENEFIT_EFFECTIVE_DATE,
      enrollmentStartDate: dayjs().format('YYYY-MM-DD'),
      enrollmentEndDate: dayjs().add(2, 'weeks').format('YYYY-MM-DD'),
      ...(initialConfig
        ? {
            companyName: initialConfig.companyName ?? '',
            salesContact: {
              fullName: initialConfig.salesContact?.fullName ?? '',
              title: initialConfig.salesContact?.title ?? '',
              phone: initialConfig.salesContact?.phone ?? '',
              email: initialConfig.salesContact?.email ?? '',
            },
          }
        : {}),
    },
  })

  useImperativeHandle(ref, () => ({
    submit: () => formRef.current?.requestSubmit(),
  }))

  return (
    <form onSubmit={handleSubmit(onSubmit)} ref={formRef}>
      <Stack space={16}>
        {/* ── Company + Enrollment — single row ── */}
        <ContentSection title="Company & Enrollment">
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 12,
              alignItems: 'flex-end',
            }}
          >
            {/* Customer Name */}
            <Stack space={4}>
              <Typography size={13}>
                Company Name
                <Typography color={TextColors.error}> *</Typography>
              </Typography>
              <Stack direction="row" space={0} align="center">
                <div
                  style={{
                    padding: '4px 8px',
                    background: '#f5f5f5',
                    border: '1px solid #d9d9d9',
                    borderRight: 'none',
                    borderRadius: '6px 0 0 6px',
                    whiteSpace: 'nowrap',
                    lineHeight: '30px',
                    fontSize: 13,
                    color: '#8c8c8c',
                  }}
                >
                  {DEMO_NAME_PREFIX}
                </div>
                <RHFInput
                  control={control}
                  name="companyName"
                  placeholder="Prospect name"
                  size="middle"
                  style={{ width: 180, borderRadius: '0 6px 6px 0' }}
                />
              </Stack>
            </Stack>

            {/* Payroll cycle and enrollment dates are fixed at creation time,
                so in edit mode they show inline as text instead of inputs. */}
            {isEditMode ? (
              // Grouped and divided from the name input so the static context
              // reads as a set rather than as four more form fields.
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 28,
                  alignItems: 'flex-end',
                  marginLeft: 12,
                  paddingLeft: 24,
                  borderLeft: '1px solid #f0f0f0',
                }}
              >
                <ReadOnlyField
                  label="Payroll Cycle"
                  width={READ_ONLY_FIELD_WIDTH}
                  value={payrollCycleLabel(initialConfig?.payrollCycle)}
                />
                <ReadOnlyField
                  label="Benefit Effective"
                  width={READ_ONLY_FIELD_WIDTH}
                  value={
                    enrollmentLoading
                      ? '…'
                      : initialConfig?.benefitEffectiveDate
                  }
                />
                <ReadOnlyField
                  label="Enroll Start"
                  width={READ_ONLY_FIELD_WIDTH}
                  value={
                    enrollmentLoading ? '…' : initialConfig?.enrollmentStartDate
                  }
                />
                <ReadOnlyField
                  label="Enroll End"
                  width={READ_ONLY_FIELD_WIDTH}
                  value={
                    enrollmentLoading ? '…' : initialConfig?.enrollmentEndDate
                  }
                />
              </div>
            ) : (
              <>
                {/* Payroll Cycle */}
                <Stack space={4}>
                  <Typography size={13}>
                    Payroll Cycle
                    <Typography color={TextColors.error}> *</Typography>
                  </Typography>
                  <RHFSelect
                    control={control}
                    name="payrollCycle"
                    options={PAYROLL_CYCLE_OPTIONS}
                    defaultValue="12"
                    size="middle"
                    style={{ width: 160 }}
                  />
                </Stack>

                {/* Benefit Effective Date */}
                <Stack space={4}>
                  <Typography size={13}>
                    Benefit Effective
                    <Typography color={TextColors.error}> *</Typography>
                  </Typography>
                  <RHFDatePicker
                    control={control}
                    name="benefitEffectiveDate"
                    style={{ width: 148 }}
                  />
                </Stack>

                {/* Enrollment Start */}
                <Stack space={4}>
                  <Typography size={13}>
                    Enroll Start
                    <Typography color={TextColors.error}> *</Typography>
                  </Typography>
                  <RHFDatePicker
                    control={control}
                    name="enrollmentStartDate"
                    style={{ width: 136 }}
                  />
                </Stack>

                {/* Enrollment End */}
                <Stack space={4}>
                  <Typography size={13}>
                    Enroll End
                    <Typography color={TextColors.error}> *</Typography>
                  </Typography>
                  <RHFDatePicker
                    control={control}
                    name="enrollmentEndDate"
                    style={{ width: 136 }}
                  />
                </Stack>
              </>
            )}
          </div>
        </ContentSection>

        {/* ── SureCo Sales Contact (optional) ── */}
        <ContentSection title="Sales Contact (Optional)">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isSmallScreen
                ? '1fr'
                : 'repeat(4, minmax(0, 1fr))',
              gap: 12,
            }}
          >
            <Stack space={4}>
              <Typography size={14}>Contact Name</Typography>
              <RHFInput
                control={control}
                name="salesContact.fullName"
                placeholder="Jane Smith"
                size="middle"
              />
            </Stack>

            <Stack space={4}>
              <Typography size={14}>Title</Typography>
              <RHFInput
                control={control}
                name="salesContact.title"
                placeholder="Regional Sales Director"
                size="middle"
              />
            </Stack>

            <Stack space={4}>
              <Typography size={14}>Phone</Typography>
              <RHFMobileInput
                control={control}
                name="salesContact.phone"
                shouldAllowEmpty
              />
            </Stack>

            <Stack space={4}>
              <Typography size={14}>Email</Typography>
              <RHFInput
                control={control}
                name="salesContact.email"
                placeholder="jsmith@surecolabs.com"
                size="middle"
              />
            </Stack>
          </div>
        </ContentSection>
      </Stack>
    </form>
  )
})

DemoCustomerCreateForm.displayName = 'DemoCustomerCreateForm'
export default DemoCustomerCreateForm
